import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, Clock, Loader2, MessageCircleQuestion } from 'lucide-react';
import { fetchBhriguRecentFeed } from '../../lib/askBhriguApi';
import { groupBhriguRecentFeed } from '../../lib/groupBhriguRecentFeed';
import { SmoothExpand } from '../shared/SmoothExpand';

/**
 * @param {string | null | undefined} iso
 */
function formatRecentDate(iso, showEnglish) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      return showEnglish ? 'Today' : 'आज';
    }
    if (diffDays === 1) {
      return showEnglish ? 'Yesterday' : 'कल';
    }
    if (diffDays < 7) {
      return showEnglish ? `${diffDays}d ago` : `${diffDays} दिन पहले`;
    }
    return d.toLocaleDateString(showEnglish ? 'en-IN' : 'hi-IN', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

/**
 * @param {{
 *   isDark: boolean;
 *   showEnglish: boolean;
 *   refreshKey?: number;
 *   collapsible?: boolean;
 *   compact?: boolean;
 *   maxItems?: number;
 *   defaultOpen?: boolean;
 *   transparentShell?: boolean;
 *   isSignedIn?: boolean;
 *   onSelectQuestion?: (item: { id: string; question_text: string; answer_text?: string; ask_count?: number }) => void;
 * }} props
 */
export function BhriguRecentQuestions({
  isDark,
  showEnglish,
  refreshKey = 0,
  collapsible = false,
  compact = false,
  maxItems = 8,
  defaultOpen = true,
  transparentShell = false,
  isSignedIn = false,
  onSelectQuestion,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [expanded, setExpanded] = useState(defaultOpen);

  const muted = transparentShell ? 'ask-bhrigu-readable-muted' : isDark ? 'text-gray-400' : 'text-gray-600';
  const card = transparentShell
    ? 'ask-bhrigu-transparent-card'
    : isDark
      ? 'rounded-xl border border-white/10 bg-white/[0.04]'
      : 'rounded-xl border border-amber-200/80 bg-white/90 shadow-sm';
  const compactCard = transparentShell
    ? 'ask-bhrigu-transparent-card'
    : isDark
      ? 'rounded-lg border border-white/10 bg-white/[0.03]'
      : 'rounded-lg border border-amber-200/70 bg-white/80';

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const rows = await fetchBhriguRecentFeed({ limit: 20 });
      const withoutChartLab = (Array.isArray(rows) ? rows : []).filter((row) => {
        const q = String(row?.question_text || '');
        // Chart Lab sessions belong under the Chart tab, not the community recent list
        if (/^Syllabus Chart Lab:/i.test(q)) return false;
        if (row?.page_key === 'syllabus-chart-lab') return false;
        if (row?.moderation_flags?.chartLab) return false;
        return true;
      });
      setItems(groupBhriguRecentFeed(withoutChartLab).slice(0, maxItems));
    } catch {
      setItems([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [maxItems]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    setExpanded(defaultOpen);
  }, [defaultOpen]);

  const totalAsks = useMemo(() => items.reduce((sum, item) => sum + (Number(item.ask_count) || 1), 0), [items]);

  const title = showEnglish ? 'Recently asked' : 'हाल में पूछे गए';
  const emptyHint = showEnglish
    ? isSignedIn
      ? 'No community answers yet. Ask your first question below.'
      : 'No community answers yet. Be the first to ask after signing in.'
    : isSignedIn
      ? 'अभी कोई सामुदायिक उत्तर नहीं। नीचे अपना पहला प्रश्न पूछें।'
      : 'अभी कोई सामुदायिक उत्तर नहीं। साइन इन करके पहले प्रश्न पूछें।';
  const errorHint = showEnglish
    ? 'Could not load recent questions. Start the API with npm run dev.'
    : 'हाल के प्रश्न लोड नहीं हो सके। npm run dev चलाएँ।';

  const itemCard = compact ? compactCard : card;
  const itemTextClass = compact ? 'text-xs' : 'text-sm';
  const itemPadClass = compact ? 'px-2.5 py-1.5' : 'px-3 py-2.5';
  const headingClass = transparentShell
    ? 'ask-bhrigu-readable'
    : isDark
      ? 'text-violet-200'
      : 'text-violet-900';
  const itemLabelClass = transparentShell
    ? 'ask-bhrigu-readable'
    : isDark
      ? 'text-white'
      : 'text-gray-900';

  const renderItem = (item, index) => {
    const count = Number(item.ask_count) || 1;
    const stagger = compact ? '' : `bhrigu-stagger-${Math.min(index + 1, 8)}`;
    return (
      <li key={String(item.id || item.question_text)} className={compact ? '' : `bhrigu-recent-item ${stagger}`}>
        <button
          type="button"
          onClick={() =>
            onSelectQuestion?.({
              id: String(item.id),
              question_text: String(item.question_text),
              answer_text: item.answer_text ? String(item.answer_text) : undefined,
              ask_count: count,
            })
          }
          className={`${compact ? '' : 'bhrigu-recent-btn'} flex w-full items-center justify-between gap-2 rounded-lg border text-left ${itemPadClass} ${itemCard} ${
            transparentShell
              ? 'border-transparent hover:border-white/20'
              : isDark
                ? 'hover:border-violet-400/30 hover:bg-white/[0.06]'
                : 'hover:border-violet-200 hover:bg-violet-50/50'
          }`}
        >
          <span className="flex min-w-0 flex-1 items-center gap-1.5">
            <span
              className={`min-w-0 font-medium leading-snug ${itemTextClass} ${itemLabelClass} ${showEnglish ? '' : 'font-devanagari'}`}
              lang={showEnglish ? 'en' : 'hi'}
            >
              {item.question_text}
            </span>
            {count > 1 ? (
              <span
                className={`inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full px-1 text-[9px] font-bold ${
                  isDark ? 'bg-violet-500/25 text-violet-200' : 'bg-violet-100 text-violet-800'
                }`}
                title={showEnglish ? `Asked ${count} times` : `${count} बार पूछा गया`}
              >
                {count}
              </span>
            ) : null}
          </span>
          {!compact ? (
            <span className={`inline-flex shrink-0 items-center gap-1 text-[10px] ${muted}`}>
              <Clock className="h-3 w-3" aria-hidden />
              {formatRecentDate(item.created_at, showEnglish)}
            </span>
          ) : null}
        </button>
      </li>
    );
  };

  if (items.length === 0 && !loading && !collapsible) {
    return (
      <div className="bhrigu-recent-shell space-y-2">
        <h3
          className={`flex items-center gap-2 text-sm font-bold ${headingClass} ${showEnglish ? '' : 'font-devanagari'}`}
          lang={showEnglish ? 'en' : 'hi'}
        >
          <MessageCircleQuestion className="h-4 w-4 shrink-0" aria-hidden />
          {title}
        </h3>
        <p className={`rounded-xl border px-3 py-3 text-xs leading-relaxed ${card} ${muted}`}>
          {loadError ? errorHint : emptyHint}
        </p>
      </div>
    );
  }

  const body = loading ? (
    <div className={`${compact ? '' : 'bhrigu-recent-skeleton'} flex items-center gap-2 px-3 py-2 text-xs ${muted} ${compact ? '' : itemCard}`}>
      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      {showEnglish ? 'Loading…' : 'लोड हो रहा है…'}
    </div>
  ) : items.length === 0 ? (
    <p className={`px-1 py-1 text-xs leading-relaxed ${muted} ${showEnglish ? '' : 'font-devanagari'}`}>
      {loadError ? errorHint : emptyHint}
    </p>
  ) : (
    <ul className={compact ? 'space-y-1' : 'space-y-1.5'}>
      {items.map((item, index) => renderItem(item, index))}
    </ul>
  );

  if (!collapsible) {
    return (
      <div className="bhrigu-recent-shell space-y-2">
        <h3
          className={`flex items-center gap-2 text-sm font-bold ${headingClass} ${showEnglish ? '' : 'font-devanagari'}`}
          lang={showEnglish ? 'en' : 'hi'}
        >
          <MessageCircleQuestion className="h-4 w-4 shrink-0" aria-hidden />
          {title}
        </h3>
        {body}
      </div>
    );
  }

  const summaryPad = compact ? 'px-3 py-2' : 'px-4 py-3';
  const summaryText = compact ? 'text-xs font-semibold' : 'text-sm font-semibold';

  return (
    <div
      className={`rounded-xl border ${
        transparentShell
          ? 'ask-bhrigu-transparent-card border-transparent'
          : isDark
            ? 'border-white/10 bg-white/[0.03]'
            : 'border-amber-200/80 bg-white/60'
      }`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className={`flex w-full cursor-pointer items-center justify-between gap-2 ${summaryPad} ${summaryText} transition-colors duration-500 ${
          transparentShell
            ? `ask-bhrigu-readable ${isDark ? 'hover:text-white' : 'hover:text-stone-950'}`
            : isDark
              ? 'text-violet-100 hover:bg-white/[0.04]'
              : 'text-violet-900 hover:bg-violet-50/80'
        } ${showEnglish ? '' : 'font-devanagari'}`}
        lang={showEnglish ? 'en' : 'hi'}
      >
        <span className="flex items-center gap-1.5">
          <MessageCircleQuestion className={`${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} shrink-0`} aria-hidden />
          {title}
        </span>
        <span className="flex items-center gap-1.5">
          {!loading && items.length > 0 ? (
            <span className={`text-[10px] font-normal ${muted}`}>
              {showEnglish ? `${items.length} topics` : `${items.length} विषय`}
              {totalAsks > items.length ? ` · ${totalAsks}` : ''}
            </span>
          ) : loading ? (
            <Loader2 className={`h-3 w-3 animate-spin ${muted}`} aria-hidden />
          ) : loadError ? (
            <span className={`text-[10px] font-normal ${isDark ? 'text-amber-300/80' : 'text-amber-700'}`}>
              {showEnglish ? 'Offline' : 'ऑफ़लाइन'}
            </span>
          ) : null}
          <ChevronDown
            className={`bhrigu-details-chevron h-3.5 w-3.5 shrink-0 ${expanded ? 'rotate-180' : ''} ${muted}`}
            aria-hidden
          />
        </span>
      </button>
      <SmoothExpand open={expanded}>
        <div className={`max-h-36 overflow-y-auto border-t border-inherit px-2 py-2 ${transparentShell ? 'border-transparent' : isDark ? 'border-white/10' : 'border-amber-200/60'}`}>
          {body}
        </div>
      </SmoothExpand>
    </div>
  );
}

export default BhriguRecentQuestions;
